import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { recordFunderFeedback } from '../api'
import Badge from './Badge'

/**
 * One foundation lead — the dashboard list and the Foundations search render
 * through THIS component, not two copies.
 *
 * Extracted from Dashboard.jsx for homelab-kb #60: the search results and the
 * dashboard list show the same object from the same API shape
 * (`FunderMatchOut`), and two hand-maintained copies of a card is how the two
 * surfaces start disagreeing about a funder — the same class of drift the
 * backend avoids by sharing `rank_funder_rows` and `_funder_match_out`.
 */
function FunderCard({ funder, clientId, onFeedback }) {
  const [feedbackSent, setFeedbackSent] = useState(funder.feedback || null)
  const [sending, setSending] = useState(false)
  const [open, setOpen] = useState(false)

  async function handleFeedback(value) {
    if (sending) return
    setSending(true)
    try {
      await recordFunderFeedback({ client_id: clientId, funder_id: funder.funder_id, feedback: value })
      setFeedbackSent(value)
      onFeedback?.(funder.funder_id, value)
    } catch (_) {}
    setSending(false)
  }

  const location = [funder.city, funder.state].filter(Boolean).join(', ')

  return (
    <div className="bg-white rounded-lg border border-gray-200 hover:border-gray-300 hover:shadow-sm transition-all">
      {/* The name is a LINK to the full profile, not part of the expand
          toggle: list-row -> detail-page is what people already expect to
          click, and the profile was previously reachable only from a text
          link buried at the bottom of the expanded panel. An <a> cannot be
          nested inside a <button>, so the row is a flex container with two
          separate controls rather than one big button. */}
      <div className="w-full flex items-center gap-3 px-4 py-3">
        <div className="flex-shrink-0 w-10 text-center">
          <div className="text-lg font-bold text-brand-500 leading-none">{funder.score}</div>
          <div className="text-[10px] text-gray-500 mt-0.5">score</div>
        </div>
        <div className="flex-1 min-w-0">
          <Link
            to={`/funders/${funder.funder_id}`}
            className="font-medium text-gray-900 text-sm leading-snug truncate block hover:text-brand-500 hover:underline"
          >
            {funder.name}
          </Link>
          <p className="text-xs text-gray-500 mt-0.5 truncate">
            {location}
            {funder.typical_grant && <> · Typical grant: {funder.typical_grant}</>}
          </p>
        </div>
        <div className="flex-shrink-0 flex items-center gap-2">
          {/* "Has deadlines" on a cycle that closed last year is a lie the
              reader acts on. A stale filing falls back to the openness fact,
              which is still true and still filed. */}
          {funder.application_deadlines && !funder.deadlines_stale
            ? <Badge variant="soon">Has deadlines</Badge>
            : <Badge variant="open">Accepts applications</Badge>}
          <button
            onClick={() => setOpen(o => !o)}
            aria-expanded={open}
            aria-label={open ? `Hide details for ${funder.name}` : `Show details for ${funder.name}`}
            className="p-1 -m-1 text-gray-500 hover:text-gray-600"
          >
            <svg
              className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`}
              viewBox="0 0 20 20" fill="currentColor"
            >
              <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.17l3.71-3.94a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
            </svg>
          </button>
        </div>
      </div>

      {open && (
        <div className="px-4 pb-3 pt-0 space-y-3 border-t border-gray-100">
          {funder.why_matched && (
            <p className="text-sm text-gray-600 italic border-l-2 border-brand-100 pl-3 pt-3">
              {funder.why_matched}
            </p>
          )}
          <div className="space-y-1.5 text-sm text-gray-600">
            {funder.application_deadlines && (
              <p>
                <span className="font-medium text-gray-900">
                  {funder.deadlines_stale ? 'Past deadline (as filed):' : 'Deadlines:'}
                </span>{' '}
                {funder.application_deadlines}
                {funder.deadlines_stale && (
                  <span className="block text-xs text-amber-700 mt-0.5">
                    This cycle has closed. Most foundations repeat it annually, so
                    confirm the current dates before applying.
                  </span>
                )}
              </p>
            )}
            {funder.application_info && (
              <p><span className="font-medium text-gray-900">How to apply:</span> {funder.application_info}</p>
            )}
            {funder.application_contact && (
              <p><span className="font-medium text-gray-900">Contact:</span> {funder.application_contact}</p>
            )}
            {funder.restrictions && (
              <p><span className="font-medium text-gray-900">Restrictions:</span> {funder.restrictions}</p>
            )}
          </div>
          <p className="text-xs text-gray-500">
            Based on their latest IRS filing{funder.filing_year ? ` (${funder.filing_year})` : ''}
          </p>
          <div className="flex items-center gap-3">
            <Link
              to={`/funders/${funder.funder_id}`}
              className="text-sm font-medium text-brand-500 hover:underline"
            >
              Full profile →
            </Link>
            {funder.website && (
              <a
                href={funder.website}
                target="_blank"
                rel="noreferrer"
                className="text-sm font-medium text-brand-500 hover:underline"
              >
                Website →
              </a>
            )}
            <div className="flex-1" />
            {feedbackSent ? (
              <span className="text-xs text-gray-500">
                {feedbackSent === 'relevant' ? '👍 Marked relevant' : '👎 Marked not for us'}
              </span>
            ) : (
              <>
                <button
                  onClick={() => handleFeedback('relevant')}
                  disabled={sending}
                  className="text-sm text-gray-500 hover:text-green-600 disabled:opacity-50 transition-colors"
                  title="Relevant"
                >👍</button>
                <button
                  onClick={() => handleFeedback('irrelevant')}
                  disabled={sending}
                  className="text-sm text-gray-500 hover:text-red-500 disabled:opacity-50 transition-colors"
                  title="Not for us"
                >👎</button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default FunderCard
